import { useState, type FormEvent } from 'react';
import { Eye, EyeOff, Loader2, ShieldCheck } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { login } from '@/api/auth.api';

interface ManagerApprovalDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  description?: string;
  /** External busy state (the approved action running after auth). */
  submitting?: boolean;
  /** Receives the manager's short-lived access token to authorize the action. */
  onApprove: (managerToken: string) => void | Promise<void>;
}

/**
 * Manager credential override for a POS action that needs elevated permission
 * (here: rescheduling an add-on, which moves slot capacity and is audited).
 *
 * A supervisor enters THEIR OWN credentials; we authenticate them for this one
 * action and hand the resulting token to the caller — we do NOT replace the
 * cashier's session. The backend endpoint the token is used against enforces
 * the actual permission, so an under-privileged login surfaces there.
 */
export function ManagerApprovalDialog({
  open,
  onOpenChange,
  title,
  description,
  submitting,
  onApprove,
}: ManagerApprovalDialogProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [authing, setAuthing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setEmail('');
    setPassword('');
    setShowPassword(false);
    setError(null);
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password) return;
    setAuthing(true);
    setError(null);
    try {
      const res = await login(email.trim(), password);
      const token = res.tokens?.accessToken;
      if (!token) throw new Error('Login manajer gagal.');
      await onApprove(token);
      reset();
    } catch (err) {
      setError((err as Error)?.message || 'Kredensial manajer tidak valid.');
    } finally {
      setAuthing(false);
    }
  };

  const busy = authing || Boolean(submitting);

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-crimson-700" />
            {title ?? 'Persetujuan manajer'}
          </DialogTitle>
          <DialogDescription>
            {description ?? 'Tindakan ini memerlukan persetujuan supervisor.'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="mgr-email">Email manajer</Label>
            <Input
              id="mgr-email"
              type="email"
              autoFocus
              autoComplete="off"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="mgr-pass">Kata sandi</Label>
            <div className="relative">
              <Input
                id="mgr-pass"
                type={showPassword ? 'text' : 'password'}
                autoComplete="off"
                className="pr-11"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? 'Sembunyikan kata sandi' : 'Tampilkan kata sandi'}
                aria-pressed={showPassword}
                className="absolute right-1.5 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-md text-ink-400 transition-colors hover:bg-secondary hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          {error && <p className="text-xs font-medium text-destructive">{error}</p>}
          <Button
            type="submit"
            size="lg"
            className="h-12 w-full gap-2"
            disabled={busy || !email.trim() || !password}
          >
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <ShieldCheck className="h-5 w-5" />}
            Setujui &amp; jadwalkan ulang
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
